import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { playClick } from "../../lib/sounds";
import { FilePreviewModal } from "../AttachmentHoverPreview";
import { Icon } from "../Icon";
import { metaJoin } from "../MetaSeparator";
import { Text } from "../Text";
import { triggerDownload } from "./download";
import { DownloadButton } from "./DownloadButton";
import { useObjectUrl } from "./useObjectUrl";

export const BinaryBodyRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  height: 48px;
  background: var(--surface-primary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

const Thumbnail = styled("img")`
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 8px;
  border: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
`;

type Props = {
  contentType: string;
  url?: string;
  fileName?: string;
};

export function Image({ contentType, url, fileName }: Props) {
  const { blobUrl, size: fetchedSize } = useObjectUrl(url);
  const [dimensions, setDimensions] = useState<{ w: number; h: number }>();
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!blobUrl) return;
    const img = new window.Image();
    img.onload = () =>
      setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = blobUrl;
  }, [blobUrl]);

  const ext = contentType.replace("image/", "").toUpperCase();

  const downloadName = fileName ?? `image.${ext.toLowerCase()}`;
  const download = useCallback(() => {
    if (blobUrl) triggerDownload(blobUrl, downloadName);
  }, [blobUrl, downloadName]);

  const sizeForDisplay = fetchedSize;

  return (
    <>
      <BinaryBodyRow style={{ paddingLeft: 8 }}>
        {blobUrl && (
          <Thumbnail
            src={blobUrl}
            style={{ cursor: "pointer" }}
            onMouseDown={() => {
              playClick();
              setPreviewOpen(true);
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Text variant="text-regular" color="var(--text-primary)" ellipsis>
            {ext}
          </Text>
          {(dimensions || sizeForDisplay != null) && (
            <Text
              variant="text-small"
              color="var(--text-secondary)"
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {metaJoin([
                dimensions && `${dimensions.w} × ${dimensions.h}`,
                sizeForDisplay != null && formatBytes(sizeForDisplay),
              ])}
            </Text>
          )}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <DownloadButton onPress={() => setPreviewOpen(true)}>
            <Icon name="eye" size={12} />
            <Text variant="text-small-md">Preview</Text>
          </DownloadButton>
          <DownloadButton onPress={download} disabled={!blobUrl}>
            <Icon name="arrow-down" size={12} />
            <Text variant="text-small-md">Download</Text>
          </DownloadButton>
        </div>
      </BinaryBodyRow>
      <FilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileName={downloadName}
        mediaType={contentType}
        sizeBytes={sizeForDisplay ?? 0}
        url={url ?? ""}
      />
    </>
  );
}
